/**
 * Currency Display Utilities
 * Provides functions for formatting currency amounts with conversion details,
 * exchange rates, and freight cost explanations for the ROI calculator.
 */

import { Currency, CalculationResultData } from '../types/calculator.types';

/**
 * Options for currency display formatting
 */
export interface CurrencyDisplayOptions {
  showConversion?: boolean;
  originalAmount?: number;
  originalCurrency?: Currency;
  convertedAmount: number;
  convertedCurrency: Currency;
  exchangeRate?: number;
}

/**
 * Formats currency amounts with optional conversion display
 * 
 * @param options - Currency display configuration
 * @returns Formatted currency string (e.g., "$3,400" or "$3,400/C$4,760")
 * 
 * Examples:
 * - Single currency: "$3,400"
 * - Dual currency: "$3,400/C$4,760"
 * - CAD to USD: "C$4,760/$3,400"
 */
export function formatCurrencyWithConversion(options: CurrencyDisplayOptions): string {
  const { 
    showConversion, 
    originalAmount, 
    originalCurrency, 
    convertedAmount, 
    convertedCurrency,
  } = options;
  
  /**
   * Formats a currency amount with appropriate symbol
   * @param amount - Numeric amount to format
   * @param currency - Currency type (USD or CAD)
   * @returns Formatted currency string with symbol
   */
  const formatAmount = (amount: number, currency: Currency): string => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol} ${amount.toLocaleString('en-US', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    })}`;
  };
  
  // Show dual currency format when conversion occurred
  if (showConversion && 
      originalAmount !== undefined && 
      originalCurrency && 
      originalCurrency !== convertedCurrency) {
    
    const originalFormatted = formatAmount(originalAmount, originalCurrency);
    const convertedFormatted = formatAmount(convertedAmount, convertedCurrency);
    return `${originalFormatted}/${convertedFormatted}`;
  } else {
    // Show single currency format
    return formatAmount(convertedAmount, convertedCurrency);
  }
}

/**
 * Gets the appropriate currency symbol for display
 * 
 * @param currency - Currency type (USD or CAD)
 * @returns Currency symbol string
 */
export function getCurrencySymbol(currency: Currency): string {
  switch (currency) {
    case 'USD':
      return 'USD';
    case 'CAD':
      return 'CAD';
    default:
      return 'USD'; // Default fallback
  }
}

/**
 * Formats exchange rate for display
 * 
 * @param rate - Exchange rate value
 * @param fromCurrency - Source currency (defaults to CAD)
 * @param toCurrency - Target currency (defaults to USD)
 * @returns Formatted exchange rate string (e.g., "1 CAD = 0.714 USD")
 */
export function formatExchangeRate(
  rate: number, 
  fromCurrency: Currency = 'CAD', 
  toCurrency: Currency = 'USD'
): string {
  const precision = rate < 1 ? 3 : 3; // Use 3 decimal places for precision
  return `1 ${fromCurrency} = ${rate.toFixed(precision)} ${toCurrency}`;
}

/**
 * Generates freight cost explanation with currency conversion details
 * 
 * @param data - Calculation result data containing freight information
 * @returns Formatted explanation string with conversion details
 * 
 * Examples:
 * - With disclaimer: "Freight for Dallas, TX, USA ($3,400/C$4,760)"
 * - Without disclaimer: "Freight estimated using Dallas rate ($3,400/C$4,760) prorated by distance ratio"
 * - Minimum shipment: "Freight estimated using fallback rate (C$450) (minimum shipment size applied)"
 */
export function generateFreightExplanation(data: CalculationResultData): string {
  const {
    freightCost,
    currency,
    freightCurrency,
    originalFreightRate,
    originalFreightCurrency,
    referenceCity,
    distanceKm,
    minimumShipmentApplied,
    fallbackRateUsed,
    disclaimer
  } = data;
  
  // Use backend disclaimer if provided and enhance with currency conversion
  if (disclaimer) {
    let explanation = disclaimer;
    
    // Add currency conversion details to disclaimer if applicable
    if (originalFreightRate && 
        originalFreightCurrency && 
        freightCurrency && 
        originalFreightCurrency !== freightCurrency) {
      
      const currencyDisplay = formatCurrencyWithConversion({
        showConversion: true,
        originalAmount: originalFreightRate,
        originalCurrency: originalFreightCurrency,
        convertedAmount: freightCost,
        convertedCurrency: freightCurrency || currency
      });
      
      // Replace simple currency amounts in disclaimer with conversion display
      // Look for patterns like USD 3,400 or CAD 3,400 and replace with dual format
      const currencyPattern = /(USD|CAD)\s[\d,]+/g;
      explanation = explanation.replace(currencyPattern, currencyDisplay);
    }
    
    return explanation;
  }
  
  // Generate fallback explanation when no disclaimer provided
  let baseExplanation = "Freight estimated";
  
  // Add rate source information
  if (fallbackRateUsed) {
    baseExplanation += " using fallback rate";
  } else if (referenceCity) {
    baseExplanation += ` using ${referenceCity} rate`;
  }
  
  // Add rate amount with conversion if applicable
  if (originalFreightRate && originalFreightCurrency) {
    const rateDisplay = formatCurrencyWithConversion({
      showConversion: freightCurrency !== originalFreightCurrency,
      originalAmount: originalFreightRate,
      originalCurrency: originalFreightCurrency,
      convertedAmount: freightCost,
      convertedCurrency: freightCurrency || currency
    });
    baseExplanation += ` (${rateDisplay})`;
  } else {
    // Show just the final freight cost if no original rate available
    const finalCostDisplay = formatCurrencyWithConversion({
      showConversion: false,
      convertedAmount: freightCost,
      convertedCurrency: freightCurrency || currency
    });
    baseExplanation += ` (${finalCostDisplay})`;
  }
  
  // Add distance information if available
  if (distanceKm && referenceCity && !fallbackRateUsed) {
    baseExplanation += " prorated by distance ratio";
  }
  
  // Add minimum shipment note if applicable
  if (minimumShipmentApplied) {
    baseExplanation += " (minimum shipment size applied)";
  }
  
  return baseExplanation + ".";
}

/**
 * Formats a simple currency amount without conversion details
 * 
 * @param amount - Numeric amount to format
 * @param currency - Currency type
 * @returns Formatted currency string
 */
export function formatSimpleCurrency(amount: number, currency: Currency): string {
  return formatCurrencyWithConversion({
    showConversion: false,
    convertedAmount: amount,
    convertedCurrency: currency
  });
}

/**
 * Formats cost per area with appropriate unit suffix
 * 
 * @param amount - Cost per area amount
 * @param currency - Currency type
 * @param unitSystem - Unit system (imperial or metric)
 * @returns Formatted cost per area string (e.g., "$0.58/sq.ft" or "C$0.62/m²")
 */
export function formatCostPerArea(
  amount: number, 
  currency: Currency, 
  unitSystem: 'imperial' | 'metric'
): string {
  const symbol = getCurrencySymbol(currency);
  const unit = unitSystem === 'metric' ? '/m²' : '/sq.ft';
  
  return `${symbol} ${amount.toFixed(2)}${unit}`;
}

/**
 * Determines if currency conversion display should be shown
 * 
 * @param data - Calculation result data
 * @returns True if conversion details should be displayed
 */
export function shouldShowCurrencyConversion(data: CalculationResultData): boolean {
  return !!(
    data.originalFreightRate &&
    data.originalFreightCurrency &&
    data.freightCurrency &&
    data.originalFreightCurrency !== data.freightCurrency &&
    data.exchangeRateUsed
  );
}

/**
 * Gets currency conversion information for display
 * 
 * @param data - Calculation result data
 * @returns Currency conversion details or null if no conversion
 */
export function getCurrencyConversionInfo(data: CalculationResultData): {
  originalAmount: number;
  originalCurrency: Currency;
  convertedAmount: number;
  convertedCurrency: Currency;
  exchangeRate: number;
} | null {
  if (!shouldShowCurrencyConversion(data)) {
    return null;
  }
  
  return {
    originalAmount: data.originalFreightRate!,
    originalCurrency: data.originalFreightCurrency!,
    convertedAmount: data.freightCost,
    convertedCurrency: data.freightCurrency || data.currency,
    exchangeRate: data.exchangeRateUsed!
  };
}

/**
 * Error handling for currency display functions
 * Returns a safe fallback currency format when errors occur
 * 
 * @param error - Error that occurred
 * @param fallbackAmount - Amount to display as fallback
 * @param fallbackCurrency - Currency to use as fallback
 * @returns Safe fallback currency string
 */
export function handleCurrencyDisplayError(
  error: Error, 
  fallbackAmount: number, 
  fallbackCurrency: Currency
): string {
  console.warn('Currency display error:', error.message);
  
  // Return simple currency format as safe fallback
  return formatSimpleCurrency(fallbackAmount, fallbackCurrency);
}